"use client";

import { ThemeProvider } from "next-themes";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type HelpTheme = "light" | "dark";

/** Own key: the help center's choice never changes the app's theme. */
export const HELP_THEME_STORAGE_KEY = "help-theme";

function readStoredTheme(): HelpTheme {
  try {
    return window.localStorage.getItem(HELP_THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Idempotent — only touches DOM when a value actually differs. */
function applyHelpThemeHtml(theme: HelpTheme): void {
  const html = document.documentElement;
  const other: HelpTheme = theme === "dark" ? "light" : "dark";
  if (html.classList.contains(other)) html.classList.remove(other);
  if (!html.classList.contains(theme)) html.classList.add(theme);
  if (html.style.colorScheme !== theme) html.style.colorScheme = theme;
}

/**
 * Keeps `/help/*` on the help theme, portaled search results included.
 * Observer disconnects while applying, or class mutations loop synchronously.
 */
function HelpThemeLock({ theme }: { readonly theme: HelpTheme }): null {
  useEffect(() => {
    applyHelpThemeHtml(theme);
    const observer = new MutationObserver((): void => {
      observer.disconnect();
      applyHelpThemeHtml(theme);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [theme]);
  return null;
}

const HelpThemeContext = createContext<{ readonly theme: HelpTheme; readonly toggle: () => void } | null>(null);

export function useHelpTheme(): { readonly theme: HelpTheme; readonly toggle: () => void } {
  const value = useContext(HelpThemeContext);
  if (value === null) throw new Error("useHelpTheme outside HelpThemeShell");
  return value;
}

/** Light by default; the top bar toggle switches to dark and remembers it in this browser. */
export function HelpThemeShell({ children }: { readonly children: ReactNode }): ReactNode {
  // Lazy: the bootstrap script already painted the stored theme; no light flash.
  const [theme, setTheme] = useState<HelpTheme>(() => (typeof window === "undefined" ? "light" : readStoredTheme()));
  const toggle = useCallback((): void => {
    setTheme((prev) => {
      const next: HelpTheme = prev === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(HELP_THEME_STORAGE_KEY, next);
      } catch {
        // Private mode: the choice lasts until reload.
      }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);
  return (
    <ThemeProvider attribute="class" forcedTheme={theme} enableSystem={false} disableTransitionOnChange>
      <HelpThemeContext.Provider value={value}>
        <HelpThemeLock theme={theme} />
        {children}
      </HelpThemeContext.Provider>
    </ThemeProvider>
  );
}
